import { createMemo, JSX } from 'solid-js';
import { t } from '@trimble/i18n';
import { LineStyle } from '@trimble/web3d-providers';
import {
  StrokeOptionDropdown,
  type StrokeOptionDropdownOptionGroup,
} from '@trimble/ui-components/solidjs';

const LINE_STYLE_PREVIEW_CLASS: Record<LineStyle, string> = {
  [LineStyle.Solid]: 'border-solid',
  [LineStyle.Dashed]: 'border-dashed',
  [LineStyle.Dotted]: 'border-dotted',
};

interface LineStyleDropdownProps {
  value: LineStyle;
  onValueChange(value: LineStyle): void;
  disabled?: boolean;
  disabledReason?: string;
}

function LineStylePreview(props: Readonly<{ style: LineStyle }>): JSX.Element {
  return (
    <span
      class={`block w-full border-b-2 border-gray-8 ${LINE_STYLE_PREVIEW_CLASS[props.style]}`}
      aria-hidden
    />
  );
}

export const LineStyleDropdown = (
  props: LineStyleDropdownProps
): JSX.Element => {
  const options = createMemo(
    (): StrokeOptionDropdownOptionGroup<LineStyle>[] => [
      {
        children: [
          {
            value: LineStyle.Solid,
            label: t('measurements.measurementStyle.lineStyleOptions.solid'),
            testId: 'line-style-option-solid',
            renderPreview: () => <LineStylePreview style={LineStyle.Solid} />,
          },
          {
            value: LineStyle.Dashed,
            label: t('measurements.measurementStyle.lineStyleOptions.dashed'),
            testId: 'line-style-option-dashed',
            renderPreview: () => <LineStylePreview style={LineStyle.Dashed} />,
          },
          {
            value: LineStyle.Dotted,
            label: t('measurements.measurementStyle.lineStyleOptions.dotted'),
            testId: 'line-style-option-dotted',
            renderPreview: () => <LineStylePreview style={LineStyle.Dotted} />,
          },
        ],
      },
    ]
  );

  return (
    <StrokeOptionDropdown<LineStyle>
      ariaLabel={t('measurements.style')}
      value={props.value}
      onValueChange={props.onValueChange}
      options={options()}
      disabled={props.disabled}
      disabledReason={props.disabledReason}
      triggerTestId="measurement-line-style-select"
      listTestId="line-style-dropdown-list"
    />
  );
};
